import { useMutation, useQuery } from '@apollo/client';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import Button from '../../../core/components/Button.tsx';
import { url } from '../../../core/utils/link.ts';
import FormField from '../../../core/components/FormField.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import IntegerField from '../../../core/components/Fields/IntegerField.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import {
    CREATE_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION,
    GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS,
} from '../../queries.ts';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import { CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_PATH } from '../../constants.ts';
import { SALES_ORDER_ENTITY_VERSION_V2, SALES_ORDER_ENTITY_VERSION_V4 } from './constants.ts';
import AdditionalHeadersField, { AdditionalHeader } from './AdditionalHeadersField.tsx';

interface DynamicsFinanceOperationsFormModalOutletContext {
    integrations: any[];
    refetch: () => Promise<any>;
}

const FORM_DEFAULT_VALUES = {
    name: '',
    tenantId: '',
    environment: '',
    apiDataUrl: '',
    additionalHeaders: [] as AdditionalHeader[],
    clientId: '',
    clientSecret: '',
    dataAreaId: '',
    orderChannelIds: [],
    rfqChannelIds: [],
    onlyImportStockedArticles: false,
    requireValidUnits: false,
    revertOrderOnAttachmentFailure: false,
    rollbackOrderOnPushFailure: true,
    articlesHaveMultipleVersions: false,
    syncSalesQuotationsAsOffers: false,
    referenceQuotationOnSalesOrder: false,
    syncSalesAgreementsAsFrameworkContracts: false,
    disableMasterDataTransfer: false,
    salesOrderEntityVersion: SALES_ORDER_ENTITY_VERSION_V2,
    stopSalesProcessing: false,
    orderTakerPersonnelNumber: '',
    retailChannelTable: '',
};

const DynamicsFinanceOperationsFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    integration,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
    integration?: any;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;
    // OrderTakerPersonnelNumber and RetailChannelTable only exist on the V4 header entity.
    const isV4SalesOrderEntities = form.watch('salesOrderEntityVersion') === SALES_ORDER_ENTITY_VERSION_V4;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                tenantId: integration.tenantId,
                environment: integration.environment,
                apiDataUrl: integration.apiDataUrl,
                additionalHeaders: integration.additionalHeaders.map(({ name, value }) => ({ name, value })),
                clientId: integration.clientId,
                clientSecret: '', // Don't populate secret for security reasons
                dataAreaId: integration.dataAreaId,
                orderChannelIds: integration.orderChannels.map((channel) => channel.id),
                rfqChannelIds: integration.rfqChannels.map((channel) => channel.id),
                onlyImportStockedArticles: integration.onlyImportStockedArticles || false,
                requireValidUnits: integration.requireValidUnits || false,
                revertOrderOnAttachmentFailure: integration.revertOrderOnAttachmentFailure || false,
                rollbackOrderOnPushFailure: integration.rollbackOrderOnPushFailure ?? true,
                articlesHaveMultipleVersions: integration.articlesHaveMultipleVersions || false,
                syncSalesQuotationsAsOffers: integration.syncSalesQuotationsAsOffers || false,
                referenceQuotationOnSalesOrder: integration.referenceQuotationOnSalesOrder || false,
                syncSalesAgreementsAsFrameworkContracts: integration.syncSalesAgreementsAsFrameworkContracts || false,
                disableMasterDataTransfer: integration.disableMasterDataTransfer || false,
                salesOrderEntityVersion: integration.salesOrderEntityVersion || SALES_ORDER_ENTITY_VERSION_V2,
                stopSalesProcessing: integration.stopSalesProcessing || false,
                orderTakerPersonnelNumber: integration.orderTakerPersonnelNumber || '',
                retailChannelTable: integration.retailChannelTable?.toString() || '',
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];
    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_PATH)}>
                            {t('dynamicsFinanceOperations.form.cancelButton')}
                        </Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('dynamicsFinanceOperations.form.name')} error={formErrors.name}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="tenantId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('dynamicsFinanceOperations.form.tenantId')} error={formErrors.tenantId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="environment"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.environment')}
                            error={formErrors.environment}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="apiDataUrl"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('dynamicsFinanceOperations.form.apiDataUrl')} error={formErrors.apiDataUrl}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <AdditionalHeadersField control={form.control} errors={formErrors.additionalHeaders} />

                <Controller
                    name="clientId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('dynamicsFinanceOperations.form.clientId')} error={formErrors.clientId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="clientSecret"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.clientSecret')}
                            error={formErrors.clientSecret}
                        >
                            <StringField {...field} type="password" />
                        </FormField>
                    )}
                />

                <Controller
                    name="dataAreaId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('dynamicsFinanceOperations.form.dataAreaId')} error={formErrors.dataAreaId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.orderChannels')}
                            error={formErrors.orderChannelIds}
                        >
                            <CheckboxGroup
                                {...field}
                                options={
                                    orderChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="rfqChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.rfqChannels')}
                            error={formErrors.rfqChannelIds}
                        >
                            <CheckboxGroup
                                {...field}
                                options={
                                    rfqChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="onlyImportStockedArticles"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.onlyImportStockedArticles')}
                            error={formErrors.onlyImportStockedArticles}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="requireValidUnits"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.requireValidUnits')}
                            error={formErrors.requireValidUnits}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="revertOrderOnAttachmentFailure"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.revertOrderOnAttachmentFailure')}
                            error={formErrors.revertOrderOnAttachmentFailure}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="rollbackOrderOnPushFailure"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.rollbackOrderOnPushFailure')}
                            error={formErrors.rollbackOrderOnPushFailure}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="articlesHaveMultipleVersions"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.articlesHaveMultipleVersions')}
                            error={formErrors.articlesHaveMultipleVersions}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="syncSalesQuotationsAsOffers"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.syncSalesQuotationsAsOffers')}
                            error={formErrors.syncSalesQuotationsAsOffers}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="referenceQuotationOnSalesOrder"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.referenceQuotationOnSalesOrder')}
                            error={formErrors.referenceQuotationOnSalesOrder}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="syncSalesAgreementsAsFrameworkContracts"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.syncSalesAgreementsAsFrameworkContracts')}
                            error={formErrors.syncSalesAgreementsAsFrameworkContracts}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="disableMasterDataTransfer"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.disableMasterDataTransfer')}
                            error={formErrors.disableMasterDataTransfer}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="salesOrderEntityVersion"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.salesOrderEntityVersion')}
                            error={formErrors.salesOrderEntityVersion}
                            helpText={t('dynamicsFinanceOperations.form.salesOrderEntityVersionHelpText')}
                        >
                            <SelectField
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                                options={[
                                    {
                                        label: t('dynamicsFinanceOperations.form.salesOrderEntityVersionOption.v2'),
                                        value: SALES_ORDER_ENTITY_VERSION_V2,
                                    },
                                    {
                                        label: t('dynamicsFinanceOperations.form.salesOrderEntityVersionOption.v4'),
                                        value: SALES_ORDER_ENTITY_VERSION_V4,
                                    },
                                ]}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="stopSalesProcessing"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('dynamicsFinanceOperations.form.stopSalesProcessing')}
                            error={formErrors.stopSalesProcessing}
                        >
                            <BooleanField
                                {...field}
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                {isV4SalesOrderEntities && (
                    <Controller
                        name="orderTakerPersonnelNumber"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('dynamicsFinanceOperations.form.orderTakerPersonnelNumber')}
                                error={formErrors.orderTakerPersonnelNumber}
                                helpText={t('dynamicsFinanceOperations.form.orderTakerPersonnelNumberHelpText')}
                            >
                                <StringField {...field} />
                            </FormField>
                        )}
                    />
                )}

                {isV4SalesOrderEntities && (
                    <Controller
                        name="retailChannelTable"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('dynamicsFinanceOperations.form.retailChannelTable')}
                                error={formErrors.retailChannelTable}
                                helpText={t('dynamicsFinanceOperations.form.retailChannelTableHelpText')}
                            >
                                <IntegerField {...field} />
                            </FormField>
                        )}
                    />
                )}
            </form>
        </Modal>
    );
};

export const AddDynamicsFinanceOperationsFormModal = () => {
    const { t } = useTranslation('integrations');
    const { refetch } = useOutletContext<DynamicsFinanceOperationsFormModalOutletContext>();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createIntegration] = useMutation(CREATE_DYNAMICS_FINANCE_OPERATIONS_INTEGRATION, {
        refetchQueries: [GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS],
    });
    const handleCreateIntegration = async (formData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    tenantId: formData.tenantId,
                    environment: formData.environment,
                    apiDataUrl: formData.apiDataUrl,
                    additionalHeaders: formData.additionalHeaders,
                    clientId: formData.clientId,
                    clientSecret: formData.clientSecret,
                    dataAreaId: formData.dataAreaId,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                    onlyImportStockedArticles: formData.onlyImportStockedArticles,
                    requireValidUnits: formData.requireValidUnits,
                    revertOrderOnAttachmentFailure: formData.revertOrderOnAttachmentFailure,
                    rollbackOrderOnPushFailure: formData.rollbackOrderOnPushFailure,
                    articlesHaveMultipleVersions: formData.articlesHaveMultipleVersions,
                    syncSalesQuotationsAsOffers: formData.syncSalesQuotationsAsOffers,
                    referenceQuotationOnSalesOrder: formData.referenceQuotationOnSalesOrder,
                    syncSalesAgreementsAsFrameworkContracts: formData.syncSalesAgreementsAsFrameworkContracts,
                    disableMasterDataTransfer: formData.disableMasterDataTransfer,
                    salesOrderEntityVersion: formData.salesOrderEntityVersion,
                    stopSalesProcessing: formData.stopSalesProcessing,
                    orderTakerPersonnelNumber: formData.orderTakerPersonnelNumber,
                    retailChannelTable: formData.retailChannelTable ? Number(formData.retailChannelTable) : null,
                },
            });
            await refetch();
            publishToast({ description: t('dynamicsFinanceOperations.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('dynamicsFinanceOperations.createError'), status: 'error' });
        }
    };

    return (
        <DynamicsFinanceOperationsFormModal
            title={t('dynamicsFinanceOperations.form.createTitle')}
            submitButtonLabel={t('dynamicsFinanceOperations.form.createButton')}
            onSubmit={handleCreateIntegration}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_PATH))}
        />
    );
};
